import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';

const KSEF_OUTGOING_ERROR_MESSAGES: Record<string, string> = {
  KSEF_OUTGOING_PREPARE_VALIDATION_FAILED:
    'Dane faktury nie spełniają wymagań przygotowania do KSeF.',
  KSEF_OUTGOING_XSD_VALIDATION_FAILED: 'Wygenerowany dokument FA(3) nie przeszedł walidacji XSD.',
  KSEF_OUTGOING_ELIGIBILITY_PREPARE_BLOCKED:
    'Przygotowanie dokumentu zostało zablokowane przez politykę kwalifikacji KSeF.',
  KSEF_OUTGOING_ELIGIBILITY_SUBMIT_BLOCKED:
    'Wysyłka dokumentu została zablokowana przez politykę kwalifikacji KSeF.',
  KSEF_OUTGOING_AUTO_CANDIDATES_INVALID_TAKE:
    'Parametr "take" jest poza dozwolonym zakresem dla listy kandydatów.',
  StatusPollFailed: 'Nie udało się odpytać KSeF o status dokumentu. Spróbuj ponownie.',
};

interface ProblemPayloadShape {
  errorCode?: unknown;
  errors?: unknown;
}

const getErrorStatus = (error: unknown): number | string | undefined => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined;
  }

  const status = (error as { status?: unknown }).status;
  if (typeof status === 'number' || typeof status === 'string') {
    return status;
  }

  return undefined;
};

const getProblemPayload = (error: unknown): ProblemPayloadShape | null => {
  if (typeof error !== 'object' || error === null || !('data' in error)) {
    return null;
  }

  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  return data as ProblemPayloadShape;
};

const getProblemErrorCode = (error: unknown): string | undefined => {
  const payload = getProblemPayload(error);
  if (!payload || typeof payload.errorCode !== 'string' || !payload.errorCode.trim()) {
    return undefined;
  }

  return payload.errorCode.trim();
};

const getValidationErrors = (error: unknown): Record<string, string[]> | null => {
  const payload = getProblemPayload(error);
  if (!payload || typeof payload.errors !== 'object' || payload.errors === null) {
    return null;
  }

  const errors = payload.errors as Record<string, unknown>;
  const entries = Object.entries(errors)
    .filter(([, value]) => Array.isArray(value))
    .map(([key, value]) => [key, (value as unknown[]).map((item) => String(item))] as const);

  return entries.length > 0 ? Object.fromEntries(entries) : null;
};

const getMessageFromValidationErrors = (error: unknown): string | null => {
  const errors = getValidationErrors(error);
  if (!errors) {
    return null;
  }

  if (errors.take?.length) {
    return KSEF_OUTGOING_ERROR_MESSAGES.KSEF_OUTGOING_AUTO_CANDIDATES_INVALID_TAKE;
  }

  if (errors.fa3SnapshotXml?.length) {
    return KSEF_OUTGOING_ERROR_MESSAGES.KSEF_OUTGOING_XSD_VALIDATION_FAILED;
  }

  if (errors.eligibilityDecision?.length || errors.eligibilityReasonCodes?.length) {
    return 'Operacja została zablokowana przez reguły kwalifikacji dokumentu do KSeF.';
  }

  return null;
};

const getMessageFromStatus = (status: number | string | undefined): string | null => {
  if (status === undefined) {
    return null;
  }

  if (status === 'FETCH_ERROR') {
    return 'Brak połączenia z API. Sprawdź sieć lub dostępność backendu.';
  }

  if (status === 'PARSING_ERROR') {
    return 'Odpowiedź API ma nieprawidłowy format.';
  }

  if (status === 401) {
    return 'Sesja wygasła. Zaloguj się ponownie.';
  }

  if (status === 403) {
    return 'Brak uprawnień do wykonania operacji KSeF.';
  }

  if (status === 404) {
    return 'Nie znaleziono rekordu KSeF dla wskazanej faktury.';
  }

  if (status === 409) {
    return 'Konflikt stanu dokumentu. Odśwież dane i spróbuj ponownie.';
  }

  if (status === 429) {
    return 'Zbyt wiele żądań do API. Odczekaj chwilę i ponów próbę.';
  }

  if (typeof status === 'number' && status >= 500) {
    return 'Wystąpił błąd serwera podczas operacji KSeF. Spróbuj ponownie za chwilę.';
  }

  return null;
};

export const getKsefOutgoingErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const errorCode = getProblemErrorCode(error);
  if (errorCode && KSEF_OUTGOING_ERROR_MESSAGES[errorCode]) {
    return KSEF_OUTGOING_ERROR_MESSAGES[errorCode];
  }

  const validationMessage = getMessageFromValidationErrors(error);
  if (validationMessage) {
    return validationMessage;
  }

  const statusMessage = getMessageFromStatus(getErrorStatus(error));
  if (statusMessage) {
    return statusMessage;
  }

  return extractApiErrorMessage(error, fallbackMessage);
};

export const getKsefOutgoingErrorCode = (error: unknown): string | undefined => {
  return getProblemErrorCode(error);
};
